/*
 * Project & Quote API Calls
 * Note: For now this file also contains the Signage "Add Agent" API calls
 * (brokerage firms, offices, and agent creation) extracted into reusable helpers.
 */

const API_URL = process.env.REACT_APP_API_URL;

/*
 * Signage firm id (`signageFirmID` query param, or `:signageId` when it means the firm node):
 * - SIGNAGE_FIRM login: use session userId (that user is the firm).
 * - SIGNAGE_TEAM_MEMBER / signageteammember routes: fetch GET /parent-hierarchy, take the parent
 *   with license_type "SIGNAGE_FIRM" (id or _id). Pass that id here — not the team member's userId.
 *   Same pattern as SignageAddInstallation `fetchParentHierarchy`.
 */

/* ------------------------------- Data Types ------------------------------ */

export type BrokerageFirm = {
  id: string;
  firmDetails?: {
    companyName?: string;
  };
};

export type Office = {
  _id: string;
  officeName: string;
  officeType: string;
  officeAddress?: Array<{
    _id?: string;
    addressLine1?: string;
    city?: string;
    state?: string;
    country?: string;
    zip?: string;
  }>;
};

export type CreateAgentValues = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone1: string;
  phone2: string;

  // In SignageAddAgent form this field is named `statuss`
  statuss: string;

  agentID: string;
  licenseNumber: string;
  licenseExpiryDate: string;

  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;

  cityCode: string;
  stateCode: string;
  countryCode: string;

  latitude: number;
  longitude: number;
};

/* ------------------------------ Helper APIs ------------------------------ */

/** @param signageFirmID Resolved firm id — see file header note for team-member logins. */
export const fetchBrokerageFirms = async (
  token: string,
  signageFirmID: string,
  page = 1,
  limit = 10
): Promise<BrokerageFirm[]> => {
  const url =
    `${API_URL}/users-search` +
    `?licenseType=BROKERAGE_FIRM` +
    `&signageFirmID=${encodeURIComponent(signageFirmID || "")}` +
    `&page=${page}` +
    `&limit=${limit}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const raw = await response.json();

  // Normalize API response to a list.
  const list: any[] = Array.isArray(raw)
    ? raw
    : ((raw as any)?.data || (raw as any)?.results || (raw as any)?.users || []);

  if (!response.ok) {
    const message = (raw as any)?.error || (raw as any)?.message || "Failed to fetch brokerage firms";
    throw new Error(message);
  }

  return list
    .map((u: any) => ({
      id: String(u?.id ?? u?._id ?? ""),
      firmDetails: {
        companyName: u?.firmDetails?.companyName ?? u?.text ?? "",
      },
    }))
    .filter((x: BrokerageFirm) => x.id);
};

export const fetchOfficesByFirm = async (
  token: string,
  firmId: string
): Promise<Office[]> => {
  if (!firmId) return [];
  const response = await fetch(
    `${API_URL}/offices-search?created_by.id=${firmId}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  const raw = await response.json();

  const list: any[] = Array.isArray(raw)
    ? raw
    : ((raw as any)?.data || (raw as any)?.results || (raw as any)?.offices || []);

  if (!response.ok) {
    const message = (raw as any)?.error || (raw as any)?.message || "Failed to fetch offices";
    throw new Error(message);
  }

  return list.map((o: any): Office => ({
    _id: String(o?._id ?? o?.id ?? ""),
    officeName: String(o?.officeName ?? o?.name ?? ""),
    officeType: String(o?.officeType ?? ""),
    officeAddress: Array.isArray(o?.officeAddress) ? o.officeAddress : [],
  }));
};

/* ---------------------- Signage agents (Select2-style) ------------------- */

export type SignageAgentSelect2AgentDetails = {
  brokerageFirmAffiliation?: string;
  brokerageFirmID?: string;
  brokerageFirmName?: string;
  managingBroker?: string;
  agentID?: string;
  licenseNumber?: string;
  licenseExpiryDate?: string;
};

/** Row from GET /users/signage/:signageId/agents/select2 */
export type SignageAgentSelect2Item = {
  id: string;
  name?: string;
  text?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone1?: string;
  phone2?: string;
  userType?: string;
  licenseType?: string;
  status?: string;
  agentDetails?: SignageAgentSelect2AgentDetails;
};

export type SignageAgentsSelect2Response = {
  results: SignageAgentSelect2Item[];
  pagination: { more?: boolean };
};

/**
 * GET /users/signage/:signageId/agents/select2?page=&limit=&search=
 * (Same contract as SignageInstallationWorkOrder agent loader.)
 * @param signageId Signage firm node id — see file header note for team-member logins.
 */
export const fetchSignageAgentsSelect2 = async (
  token: string,
  signageId: string,
  params: { page?: number; limit?: number; search?: string }
): Promise<SignageAgentsSelect2Response> => {
  const page = params.page ?? 1;
  const limit = params.limit ?? 10;
  const search = (params.search ?? "").trim();

  const qs = new URLSearchParams();
  qs.set("page", String(page));
  qs.set("limit", String(limit));
  if (search) qs.set("search", search);

  const response = await fetch(
    `${API_URL}/users/signage/${encodeURIComponent(signageId)}/agents/select2?${qs.toString()}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  const raw = await response.json();

  if (!response.ok) {
    const message =
      (raw as any)?.error || (raw as any)?.message || "Failed to fetch agents";
    throw new Error(message);
  }

  const list: any[] = Array.isArray(raw)
    ? raw
    : ((raw as any)?.results ?? (raw as any)?.data ?? []);

  const results: SignageAgentSelect2Item[] = list.map((u: any) => ({
    ...u,
    id: String(u?.id ?? u?._id ?? ""),
    name: u?.name,
    text: u?.text,
    email: u?.email,
    firstName: u?.firstName,
    lastName: u?.lastName,
    phone1: u?.phone1,
    phone2: u?.phone2,
    userType: u?.userType,
    licenseType: u?.licenseType,
    status: u?.status,
    agentDetails: u?.agentDetails,
  })).filter((r) => r.id);

  return {
    results,
    pagination: { more: Boolean((raw as any)?.pagination?.more) },
  };
};

/* ---------------------- Assign Designer (users-search) ------------------- */

const USERS_SEARCH_TEAM_MEMBER_PAGE_SIZE = 10;

/** Option shape for react-select / AsyncPaginate (matches SignageAddInstallation team loader). */
export type SignageDesignerSelectOption = {
  value: string;
  label: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  licenseType?: string;
};

/**
 * GET /users-search?licenseType=SIGNAGE_TEAM_MEMBER&signageFirmID=&page=&limit=&search=
 * @param signageFirmID Firm id from session userId (firm login) or parent-hierarchy SIGNAGE_FIRM
 *   parent id (team-member login). See file header note.
 */
export const fetchUsersSearchSignageTeamMembers = async (
  token: string,
  signageFirmID: string,
  params: { page?: number; limit?: number; search?: string }
): Promise<{ options: SignageDesignerSelectOption[]; hasMore: boolean }> => {
  const page = params.page ?? 1;
  const limit = params.limit ?? USERS_SEARCH_TEAM_MEMBER_PAGE_SIZE;
  const search = (params.search ?? "").trim();

  const qs = new URLSearchParams();
  qs.set("licenseType", "SIGNAGE_TEAM_MEMBER");
  // Must be the owning signage firm id, not the team member's user id when applicable.
  qs.set("signageFirmID", signageFirmID);
  qs.set("page", String(page));
  qs.set("limit", String(limit));
  if (search) qs.set("search", search);

  const response = await fetch(`${API_URL}/users-search?${qs.toString()}`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  const json = await response.json();

  if (!response.ok) {
    const message =
      (json as any)?.error || (json as any)?.message || "Failed to load designers";
    throw new Error(message);
  }

  const list: any[] = Array.isArray(json)
    ? json
    : ((json as any)?.data ?? (json as any)?.results ?? []);

  const options: SignageDesignerSelectOption[] = list
    .map((u: any) => ({
      value: String(u?.id ?? u?._id ?? ""),
      label:
        `${u?.firstName || ""} ${u?.lastName || ""}`.trim() ||
        u?.text ||
        "(no name)",
      firstName: u?.firstName || "",
      lastName: u?.lastName || "",
      email: u?.email || "",
      phone: u?.phone1 || u?.phone2 || u?.phone || "",
      licenseType: String(u?.licenseType || u?.userType || "SIGNAGE_TEAM_MEMBER"),
    }))
    .filter((o) => o.value);

  const hasMore = Array.isArray(json)
    ? options.length === limit
    : Boolean((json as any)?.pagination?.more) ||
      ((json as any)?.total
        ? page * ((json as any)?.limit ?? limit) < (json as any).total
        : options.length === limit);

  return { options, hasMore };
};

export const createAgentUserWithAddress = async (
  token: string,
  args: {
    values: CreateAgentValues;
    selectedBrokerage: BrokerageFirm | null;
    selectedOffice: Office | null;
  }
): Promise<any> => {
  const { values, selectedBrokerage, selectedOffice } = args;

  // Default optional fields to empty strings to keep payload stable.
  const companyName = selectedBrokerage?.firmDetails?.companyName || "";
  const brokerageFirmID = selectedBrokerage?.id || "";

  const officeId = selectedOffice?._id || "";
  const officeName = selectedOffice?.officeName || "";
  const officeType = selectedOffice?.officeType || "";
  const officeAddress0 = selectedOffice?.officeAddress?.[0];
  const officeAddressId = officeAddress0?._id || "";
  const officeAddressLine1 = officeAddress0?.addressLine1 || "";
  const officeAddressCity = officeAddress0?.city || "";
  const officeAddressState = officeAddress0?.state || "";
  const officeAddressCountry = officeAddress0?.country || "";
  const officeAddressZip = officeAddress0?.zip || "";

  const response = await fetch(`${API_URL}/user/create`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      email: values.email,
      password: values.password,
      userType: "agent",
      firstName: values.firstName,
      lastName: values.lastName,
      phone1: values.phone1,
      phone2: values.phone2,
      licenseType: "AGENT",
      status: values.statuss,
      firmDetails: {
        companyID: "",
        companyName: "",
        companyWebsite: "",
        address: {
          addressLine1: values.addressLine1,
          addressLine2: values.addressLine2,
          city: values.city,
          state: values.state,
          country: values.country,
          zip: values.zipcode,
          cityCode: values.cityCode,
          stateCode: values.stateCode,
          countryCode: values.countryCode,
          latitude: values.latitude,
          longitude: values.longitude,
          phone: values.phone1,
        },
      },
      agentDetails: {
        brokerageFirmAffiliation: companyName,
        brokerageFirmName: companyName,
        brokerageFirmID: brokerageFirmID,
        agentID: values.agentID,
        licenseNumber: values.licenseNumber,
        licenseExpiryDate: values.licenseExpiryDate,
        licenseActive: false,
      },
      offices: [
        {
          _id: officeId,
          officeName,
          officeType,
          officeAddress: {
            _id: officeAddressId,
            addressLine1: officeAddressLine1,
            city: officeAddressCity,
            state: officeAddressState,
            country: officeAddressCountry,
            zip: officeAddressZip,
          },
        },
      ],
      created_by: {
        id: brokerageFirmID,
        name: companyName,
      },
    }),
  });

  const result: any = await response.json();

  if (!response.ok || result?.error) {
    const message = result?.error || result?.message || "Failed to save data";
    throw new Error(message);
  }

  // The original page posts address details, but does not block success on failure.
  try {
    const addressResponse = await fetch(
      `${API_URL}/addresses/user/${result.id}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: `${result.id}`,
          label: "",
          first_name: values.firstName,
          last_name: values.lastName,
          company: "",
          address_details: {
            label: "",
            addressLine1: values.addressLine1,
            addressLine2: values.addressLine2,
            city: values.city,
            state: values.state,
            zip: values.zipcode,
            country: values.country,
            phone: values.phone1,
            cityCode: values.cityCode,
            stateCode: values.stateCode,
            countryCode: values.countryCode,
            latitude: values.latitude,
            longitude: values.longitude,
          },
        }),
      }
    );

    const address: any = await addressResponse.json();

    if (!addressResponse.ok || address?.error) {
      const message =
        address?.error || address?.message || "Failed to save data";
      throw new Error(message);
    }
  } catch (addressErr: any) {
    // Keep behavior aligned with SignageAddAgent: log but don't block creation.
    console.error(addressErr);
  }

  return result;
};

/* --------------------------- Signage project create ------------------------ */

/** Matches backend `Address` JSON (camelCase). */
export type SignageProjectAddressPayload = {
  label: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  zip: string;
  cityCode: string;
  stateCode: string;
  countryCode: string;
  latitude: number;
  longitude: number;
  phone: string;
};

function toSignageProjectAddressPayload(
  partial: Partial<SignageProjectAddressPayload> & Record<string, unknown>
): SignageProjectAddressPayload {
  const lat = Number(partial.latitude ?? 0);
  const lng = Number(partial.longitude ?? 0);
  return {
    label: String(partial.label ?? ""),
    addressLine1: String(partial.addressLine1 ?? ""),
    addressLine2: String(partial.addressLine2 ?? ""),
    city: String(partial.city ?? ""),
    state: String(partial.state ?? ""),
    country: String(partial.country ?? ""),
    zip: String(partial.zip ?? ""),
    cityCode: String(partial.cityCode ?? ""),
    stateCode: String(partial.stateCode ?? ""),
    countryCode: String(partial.countryCode ?? ""),
    latitude: Number.isFinite(lat) ? lat : 0,
    longitude: Number.isFinite(lng) ? lng : 0,
    phone: String(partial.phone ?? ""),
  };
}

function extractAddressFromProjectSearchItem(item: unknown): Partial<SignageProjectAddressPayload> {
  if (!item || typeof item !== "object") return {};
  const it = item as Record<string, unknown>;
  const offices = it.offices as Record<string, unknown> | undefined;
  if (!offices || typeof offices !== "object") return {};
  const arr = offices.officeAddress as unknown;
  const first =
    Array.isArray(arr) && arr.length > 0 && arr[0] && typeof arr[0] === "object"
      ? (arr[0] as Record<string, unknown>)
      : null;
  if (first) {
    return {
      addressLine1: String(first.addressLine1 ?? ""),
      addressLine2: String(first.addressLine2 ?? ""),
      city: String(first.city ?? ""),
      state: String(first.state ?? ""),
      country: String(first.country ?? ""),
      zip: String(first.zip ?? ""),
      cityCode: String(first.cityCode ?? ""),
      stateCode: String(first.stateCode ?? ""),
      countryCode: String(first.countryCode ?? ""),
      latitude: Number(first.latitude ?? 0) || 0,
      longitude: Number(first.longitude ?? 0) || 0,
      phone: String(first.phone ?? ""),
      label: String(first.label ?? ""),
    };
  }
  if (typeof offices.addressLine1 === "string" || typeof offices.city === "string") {
    return {
      addressLine1: String(offices.addressLine1 ?? ""),
      addressLine2: String(offices.addressLine2 ?? ""),
      city: String(offices.city ?? ""),
      state: String(offices.state ?? ""),
      country: String(offices.country ?? ""),
      zip: String(offices.zip ?? ""),
      cityCode: String(offices.cityCode ?? ""),
      stateCode: String(offices.stateCode ?? ""),
      countryCode: String(offices.countryCode ?? ""),
      latitude: Number(offices.latitude ?? 0) || 0,
      longitude: Number(offices.longitude ?? 0) || 0,
      phone: String(offices.phone ?? ""),
      label: String(offices.officeName ?? offices.label ?? ""),
    };
  }
  return {};
}

/**
 * Resolves a full address payload for POST /projects: search row first, then office list by firm.
 */
export async function resolveSignageProjectAddressForCreate(
  token: string,
  opts: {
    searchItem: unknown;
    office: { _id: string; officeName?: string; name?: string } | null;
    brokerageFirmId: string;
    projectTitle: string;
    customerPhone: string;
  }
): Promise<SignageProjectAddressPayload> {
  const { searchItem, office, brokerageFirmId, projectTitle, customerPhone } = opts;
  const labelDefault = (office?.officeName || office?.name || projectTitle || "").trim();
  const base = toSignageProjectAddressPayload({
    label: labelDefault,
    phone: customerPhone || "",
  });

  const fromItem = extractAddressFromProjectSearchItem(searchItem);
  if ((fromItem.addressLine1 || "").trim() || (fromItem.city || "").trim()) {
    return toSignageProjectAddressPayload({
      ...base,
      ...fromItem,
      label: (fromItem.label || "").trim() || labelDefault,
      phone: (fromItem.phone || "").trim() || customerPhone || "",
    });
  }

  const oid = (office?._id || "").trim();
  const fid = (brokerageFirmId || "").trim();
  if (oid && fid) {
    try {
      const offices = await fetchOfficesByFirm(token, fid);
      const match = offices.find((o) => o._id === oid);
      const addr0 = match?.officeAddress?.[0] as Record<string, unknown> | undefined;
      if (addr0 && typeof addr0 === "object") {
        return toSignageProjectAddressPayload({
          label: labelDefault || match?.officeName || "",
          addressLine1: String(addr0.addressLine1 ?? ""),
          addressLine2: String(addr0.addressLine2 ?? ""),
          city: String(addr0.city ?? ""),
          state: String(addr0.state ?? ""),
          country: String(addr0.country ?? ""),
          zip: String(addr0.zip ?? ""),
          cityCode: String(addr0.cityCode ?? ""),
          stateCode: String(addr0.stateCode ?? ""),
          countryCode: String(addr0.countryCode ?? ""),
          latitude: Number(addr0.latitude ?? 0) || 0,
          longitude: Number(addr0.longitude ?? 0) || 0,
          phone: String(addr0.phone ?? customerPhone ?? ""),
        });
      }
    } catch {
      // best-effort only
    }
  }

  return base;
}

/**
 * POST /projects — party fields depend on selected license type (backend omits unused keys).
 * - AGENT / BROKERAGE_AGENT / SIGNAGE_AGENT: agent_id, agent_name, customer_*
 * - SIGNAGE_TEAM_MEMBER / BROKERAGE_TEAM_MEMBER / TEAM_MEMBER: brokerage_*, office, team_member_*, customer_*
 * - BROKERAGE_FIRM: brokerage_*, office, customer_* (no agent / team member)
 */
export type CreateSignageProjectBody = {
  title: string;
  notes: string;
  po_number?: string;
  vendor_id: string;
  designer_id: string;
  designer_name: string;
  send_email: boolean;
  address: SignageProjectAddressPayload;
  agent_id?: string;
  agent_name?: string;
  team_member_id?: string;
  team_member_name?: string;
  brokerage_firm_id?: string;
  brokerage_firm_name?: string;
  office?: { _id: string; officeName: string };
  customer_id?: string;
  customer_email: string;
  customer_name: string;
  customer_phone: string;
  first_name: string;
  last_name: string;
  /** Selected party type from project search (e.g. AGENT, BROKERAGE_TEAM_MEMBER). */
  license_type?: string;
  mute_messages_to_client?: boolean;
  send_message_to_client?: boolean;
};

/** POST /projects — signage vendor creates a design project. */
export const createSignageProject = async (
  token: string,
  body: CreateSignageProjectBody
): Promise<unknown> => {
  const response = await fetch(`${API_URL}/projects`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  const raw = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message =
      (raw as any)?.error || (raw as any)?.message || "Failed to create project";
    throw new Error(typeof message === "string" ? message : "Failed to create project");
  }

  return raw;
};

/** Normalize create-project response to a project id (handles common API shapes). */
export const parseCreatedProjectId = (response: unknown): string => {
  const r = response as Record<string, unknown> | null;
  if (!r || typeof r !== "object") return "";
  const data = r.data as Record<string, unknown> | undefined;
  const project = r.project as Record<string, unknown> | undefined;
  const id =
    (r.id as string) ??
    (r._id as string) ??
    (data?.id as string) ??
    (data?._id as string) ??
    (project?.id as string) ??
    (project?._id as string) ??
    "";
  return id ? String(id) : "";
};

/**
 * POST /signage/:signageId/projects/:projectId/files
 * multipart field: files[] (repeat per file)
 */
export const uploadSignageProjectFiles = async (
  token: string,
  signageId: string,
  projectId: string,
  files: File[],
  opts?: { is_proof?: boolean; version?: number; message_id?: string }
): Promise<unknown> => {
  if (!files.length) return null;
  const fd = new FormData();
  files.forEach((f) => fd.append("files[]", f));
  if (opts?.is_proof === true) fd.append("is_proof", "true");
  if (typeof opts?.version === "number" && Number.isFinite(opts.version)) {
    fd.append("version", String(opts.version));
  }
  if (typeof opts?.message_id === "string") fd.append("message_id", opts.message_id);
  const response = await fetch(
    `${API_URL}/signage/${encodeURIComponent(signageId)}/projects/${encodeURIComponent(projectId)}/files`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: fd,
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      (raw as any)?.error || (raw as any)?.message || "Failed to upload files";
    throw new Error(typeof message === "string" ? message : "Failed to upload files");
  }
  return raw;
};

/** Collect file ids from one array-shaped slice of an upload response. */
function collectIdsFromUploadItems(items: unknown): string[] {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => {
      if (typeof item === "string" && item.trim()) return item.trim();
      if (!item || typeof item !== "object") return "";
      const o = item as Record<string, unknown>;
      const id = o._id ?? o.id ?? o.file_id ?? o.fileId;
      if (typeof id === "string" && id.trim()) return id.trim();
      if (typeof id === "number" && Number.isFinite(id)) return String(id);
      return "";
    })
    .filter((id): id is string => id.length > 0);
}

/** Parse IDs from POST .../files response (handles nested `data` and alternate keys). */
export const extractUploadedFileIds = (raw: unknown): string[] => {
  if (raw == null || typeof raw !== "object") return [];
  const r = raw as Record<string, unknown>;
  const data = r.data as Record<string, unknown> | undefined;

  const buckets: unknown[] = [
    r.uploaded_items,
    r.uploaded_files,
    r.files,
    r.file_ids,
    r.ids,
    data?.uploaded_items,
    data?.uploaded_files,
    data?.files,
    data?.file_ids,
    data?.items,
  ];

  const seen = new Set<string>();
  const out: string[] = [];
  for (const b of buckets) {
    for (const id of collectIdsFromUploadItems(b)) {
      if (!seen.has(id)) {
        seen.add(id);
        out.push(id);
      }
    }
  }
  return out;
};

export type PostProjectMessageOpts = {
  /** WhatsApp-style reply: server stores thread link to the quoted message. */
  reply_to_message_id?: string;
  reply_to_file_ids?: string[];
};

/** POST /projects/:projectId/messages */
export const postProjectMessage = async (
  token: string,
  projectId: string,
  message: string,
  opts?: PostProjectMessageOpts
): Promise<unknown> => {
  const trimmed = message.trim();
  const fileIds = (opts?.reply_to_file_ids ?? []).filter(
    (id): id is string => typeof id === "string" && id.length > 0
  );
  const replyToMsgId = (opts?.reply_to_message_id ?? "").trim();
  if (!trimmed && fileIds.length === 0) return null;

  const payload: Record<string, unknown> = {
    message: trimmed || "",
  };
  if (replyToMsgId) payload.reply_to_message_id = replyToMsgId;
  if (fileIds.length > 0) payload.reply_to_file_ids = fileIds;

  const response = await fetch(
    `${API_URL}/projects/${encodeURIComponent(projectId)}/messages`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg =
      (raw as any)?.error || (raw as any)?.message || "Failed to send message";
    throw new Error(typeof msg === "string" ? msg : "Failed to send message");
  }
  return raw;
};

/** POST /projects/:projectId/approve-proof */
export const approveProjectProofVersion = async (
  token: string,
  projectId: string,
  version: number
): Promise<unknown> => {
  const response = await fetch(
    `${API_URL}/projects/${encodeURIComponent(projectId)}/approve-proof`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ version }),
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg =
      (raw as any)?.error || (raw as any)?.message || "Failed to approve proof version";
    throw new Error(typeof msg === "string" ? msg : "Failed to approve proof version");
  }
  return raw;
};

/** POST /projects/:projectId/unapprove-proof */
export const unapproveProjectProofVersions = async (
  token: string,
  projectId: string,
  versions: number[]
): Promise<unknown> => {
  const normalized = Array.from(
    new Set(
      (Array.isArray(versions) ? versions : [])
        .map((v) => Number(v))
        .filter((v) => Number.isFinite(v) && v > 0)
        .map((v) => Math.floor(v))
    )
  );
  if (normalized.length === 0) {
    throw new Error("At least one proof version is required.");
  }

  const response = await fetch(
    `${API_URL}/projects/${encodeURIComponent(projectId)}/unapprove-proof`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ versions: normalized }),
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg =
      (raw as any)?.error || (raw as any)?.message || "Failed to unapprove proof version(s)";
    throw new Error(typeof msg === "string" ? msg : "Failed to unapprove proof version(s)");
  }
  return raw;
};

/** PUT /projects/:projectId */
export const updateProjectLinkedOrderType = async (
  token: string,
  projectId: string,
  payload: {
    title?: string;
    notes?: string;
    linked_order_type?: "sales_order" | "service_order";
    agent_id?: string;
    agent_name?: string;
    team_member_id?: string;
    team_member_name?: string;
    customer_id?: string;
    customer_email?: string;
    customer_name?: string;
    customer_phone?: string;
    first_name?: string;
    last_name?: string;
    brokerage_firm_id?: string;
    brokerage_firm_name?: string;
    office?: { _id: string; officeName?: string; name?: string };
    address?: SignageProjectAddressPayload;
    /** Selected party type from project search / stored project (e.g. AGENT, BROKERAGE_TEAM_MEMBER). */
    license_type?: string;
    /** Same as POST /projects — assigned signage team designer. */
    designer_id?: string;
    designer_name?: string;
    /** Notify agent on update when supported by API (parity with create). */
    send_email?: boolean;
    /** Mute/unmute conversation email notifications to client. */
    send_message_to_client?: boolean;
  }
): Promise<unknown> => {
  const response = await fetch(`${API_URL}/projects/${encodeURIComponent(projectId)}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg =
      (raw as any)?.error || (raw as any)?.message || "Failed to update project";
    throw new Error(typeof msg === "string" ? msg : "Failed to update project");
  }
  return raw;
};

/** PATCH /projects/:projectId — mute/unmute client message email notifications */
export const updateProjectSendMessageToClient = async (
  token: string,
  projectId: string,
  sendMessageToClient: boolean
): Promise<unknown> => {
  const send = Boolean(sendMessageToClient);
  const response = await fetch(`${API_URL}/projects/${encodeURIComponent(projectId)}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      send_message_to_client: send,
      mute_messages_to_client: !send,
    }),
  });
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg =
      (raw as any)?.error || (raw as any)?.message || "Failed to update project";
    throw new Error(typeof msg === "string" ? msg : "Failed to update project");
  }
  return raw;
};

export const linkProjectQuote = async (
  token: string,
  projectId: string,
  payload: {
    linked_order_id: string;
    linked_order_number: string;
    linked_order_type: "sales_order" | "service_order";
  }
): Promise<unknown> => {
  const response = await fetch(`${API_URL}/projects/${encodeURIComponent(projectId)}/link-quote`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg = (raw as any)?.error || (raw as any)?.message || "Failed to link project quote";
    throw new Error(typeof msg === "string" ? msg : "Failed to link project quote");
  }
  return raw;
};

/** DELETE /projects/:projectId/linked-quote — clear project↔order quote link. */
export const unlinkProjectQuote = async (
  token: string,
  projectId: string
): Promise<unknown> => {
  const response = await fetch(
    `${API_URL}/projects/${encodeURIComponent(projectId)}/linked-quote`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg = (raw as any)?.error || (raw as any)?.message || "Failed to unlink project quote";
    throw new Error(typeof msg === "string" ? msg : "Failed to unlink project quote");
  }
  return raw;
};

export type ProjectPublicAccessResponse = {
  success?: boolean;
  data?: {
    project_id?: string;
    access_token?: string;
    public_path?: string;
    public_url?: string;
  };
};

/** GET /projects/:projectId/public-access */
export const fetchProjectPublicAccess = async (
  token: string,
  projectId: string
): Promise<ProjectPublicAccessResponse> => {
  const response = await fetch(
    `${API_URL}/projects/${encodeURIComponent(projectId)}/public-access`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg =
      (raw as any)?.error || (raw as any)?.message || "Failed to fetch project public access";
    throw new Error(typeof msg === "string" ? msg : "Failed to fetch project public access");
  }
  return raw as ProjectPublicAccessResponse;
};

/** POST /payments/stripe/payment-intent/:userId — amount in smallest currency unit (e.g. cents for USD). */
export type StripePaymentIntentResponse = {
  client_secret?: string;
};

export const createStripePaymentIntent = async (
  token: string,
  userId: string,
  params: { amount: number; currency?: string }
): Promise<StripePaymentIntentResponse> => {
  const response = await fetch(
    `${API_URL}/payments/stripe/payment-intent/${encodeURIComponent(userId)}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        amount: params.amount,
        currency: params.currency ?? "usd",
      }),
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg =
      (raw as any)?.error || (raw as any)?.message || "Failed to create Stripe payment intent";
    throw new Error(typeof msg === "string" ? msg : "Failed to create Stripe payment intent");
  }
  return raw as StripePaymentIntentResponse;
};

/** GET /payments/gateway/created-by/:userId — JWT Bearer. Response is a JSON array or { data: [...] }. */
export const fetchPaymentGatewaysCreatedByUserId = async (
  token: string,
  userId: string
): Promise<any[]> => {
  const response = await fetch(
    `${API_URL}/payments/gateway/created-by/${encodeURIComponent(userId)}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg =
      (raw as any)?.error || (raw as any)?.message || "Failed to fetch payment gateways";
    throw new Error(typeof msg === "string" ? msg : "Failed to fetch payment gateways");
  }
  if (Array.isArray(raw)) return raw;
  if (Array.isArray((raw as any)?.data)) return (raw as any).data;
  return [];
};

/* ------------------------ Public project review portal APIs ---------------- */

export const fetchPublicProjectById = async (
  token: string,
  projectId: string,
  publicToken: string
): Promise<SignageProjectDetail> => {
  const qs = new URLSearchParams();
  if (publicToken) qs.set("token", publicToken);
  const response = await fetch(
    `${API_URL}/public/projects/${encodeURIComponent(projectId)}${qs.toString() ? `?${qs}` : ""}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg = (raw as any)?.error || (raw as any)?.message || "Failed to load public project";
    throw new Error(typeof msg === "string" ? msg : "Failed to load public project");
  }
  return raw as SignageProjectDetail;
};

export type PublicAddressCreateValues = {
  customerId: string;
  label?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone?: string;
  cityCode?: string;
  stateCode?: string;
  countryCode?: string;
  latitude?: number;
  longitude?: number;
  officeId?: string;
  userType?: string;
};

const US_COUNTRY_DEFAULTS = { country: "United States", countryCode: "US" } as const;

/**
 * POST /public/addresses/user/:ownerId?token=
 * Used by public design review portal when project has no address yet.
 * When officeId is set, owner is the office: omit user_id from the body (office id is only in the URL).
 */
export const createPublicAddressForProjectUser = async (
  token: string,
  ownerId: string,
  publicToken: string,
  values: PublicAddressCreateValues
): Promise<unknown> => {
  const qs = new URLSearchParams();
  if (publicToken) qs.set("token", publicToken);
  const withDefault = (v: unknown) => String(v ?? "").trim();
  const withCountryDefault = (v: unknown) => withDefault(v) || US_COUNTRY_DEFAULTS.country;
  const withCountryCodeDefault = (v: unknown) => withDefault(v) || US_COUNTRY_DEFAULTS.countryCode;
  const toCoordinate = (v: unknown) => {
    const coordinate = Number(v ?? 0);
    return Number.isFinite(coordinate) ? coordinate : 0;
  };
  const officeId = withDefault(values.officeId);
  const pathId = officeId || withDefault(ownerId) || withDefault(values.customerId);
  const userTypeRaw = withDefault(values.userType);
  const isOffice = Boolean(officeId) || userTypeRaw.toUpperCase() === "OFFICE";
  const userType = isOffice ? "OFFICE" : userTypeRaw;
  const label = "";
  const firstName = withDefault(values.firstName) || (isOffice ? "Office Address" : "");
  const phone = withDefault(values.phone) || "0000000000";

  const body: Record<string, unknown> = {
    label,
    first_name: firstName,
    last_name: withDefault(values.lastName),
    company: withDefault(values.company),
    ...(userType ? { user_type: userType } : {}),
    address_details: {
      label,
      addressLine1: withDefault(values.addressLine1),
      addressLine2: withDefault(values.addressLine2),
      city: withDefault(values.city),
      state: withDefault(values.state),
      zip: withDefault(values.zip),
      country: withCountryDefault(values.country),
      phone,
      cityCode: withDefault(values.cityCode),
      stateCode: withDefault(values.stateCode),
      countryCode: withCountryCodeDefault(values.countryCode),
      latitude: toCoordinate(values.latitude),
      longitude: toCoordinate(values.longitude),
    },
  };
  if (!officeId) {
    body.user_id = withDefault(values.customerId) || withDefault(ownerId);
  }

  const response = await fetch(
    `${API_URL}/public/addresses/user/${encodeURIComponent(pathId)}${qs.toString() ? `?${qs}` : ""}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok || (raw as any)?.error) {
    const msg = (raw as any)?.error || (raw as any)?.message || "Failed to create addresses";
    throw new Error(typeof msg === "string" ? msg : "Failed to create addresses");
  }
  return raw;
};

export type PublicUserAddress = {
  _id?: string;
  id?: string;
  user_id?: string;
  label?: string;
  first_name?: string;
  last_name?: string;
  company?: string;
  default_address?: boolean;
  address_details?: {
    _id?: string;
    label?: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    country?: string;
    zip?: string;
    cityCode?: string;
    stateCode?: string;
    countryCode?: string;
    latitude?: number;
    longitude?: number;
    phone?: string;
  };
};

/** Owner id for GET /public/addresses/user/:id and GET /user-addresses/:id.
 * 1) team_member_id + office (no agent_id) → office id
 * 2) team_member_id + agent_id + office → agent_id
 * 3) agent_id only → agent_id
 */
export function resolveProjectAddressOwnerId(
  project: Record<string, unknown> | null | undefined
): string {
  if (!project) return "";
  const pickId = (...values: unknown[]) => {
    for (const value of values) {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        const nested = value as Record<string, unknown>;
        const nestedId = String(nested._id ?? nested.id ?? "").trim();
        if (nestedId && nestedId !== "000000000000000000000000") return nestedId;
        continue;
      }
      const id = String(value ?? "").trim();
      if (id && id !== "000000000000000000000000") return id;
    }
    return "";
  };
  const office = (project.office || {}) as Record<string, unknown>;
  const officeId = pickId(office._id, office.id, project.office_id, project.officeId);
  const teamMemberId = pickId(project.team_member_id, project.teamMemberId);
  const agentId = pickId(project.agent_id, project.agentId);

  if (teamMemberId && officeId && agentId) return agentId;
  if (teamMemberId && officeId) return officeId;
  if (agentId) return agentId;
  return "";
}

/** GET /public/user-addresses/:userId (public route; no bearer required). */
export const fetchPublicUserAddresses = async (
  userId: string
): Promise<PublicUserAddress[]> => {
  const uid = String(userId || "").trim();
  if (!uid) return [];
  const response = await fetch(
    `${API_URL}/public/addresses/user/${encodeURIComponent(uid)}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    }
  );
  const raw = await response.json().catch(() => ([]));
  if (!response.ok) {
    const msg = (raw as any)?.error || (raw as any)?.message || "Failed to fetch public addresses";
    throw new Error(typeof msg === "string" ? msg : "Failed to fetch public addresses");
  }
  if (Array.isArray(raw)) return raw as PublicUserAddress[];
  if (Array.isArray((raw as any)?.data)) return (raw as any).data as PublicUserAddress[];
  if (Array.isArray((raw as any)?.results)) return (raw as any).results as PublicUserAddress[];
  return [];
};

/** GET /user-addresses/:userId (private route; bearer token required). */
export const fetchUserAddressesByUserId = async (
  token: string,
  userId: string
): Promise<PublicUserAddress[]> => {
  const uid = String(userId || "").trim();
  if (!uid) return [];
  const response = await fetch(`${API_URL}/user-addresses/${encodeURIComponent(uid)}`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  const raw = await response.json().catch(() => ([]));
  if (!response.ok) {
    const msg = (raw as any)?.error || (raw as any)?.message || "Failed to fetch addresses";
    throw new Error(typeof msg === "string" ? msg : "Failed to fetch addresses");
  }
  if (Array.isArray(raw)) return raw as PublicUserAddress[];
  if (Array.isArray((raw as any)?.data)) return (raw as any).data as PublicUserAddress[];
  if (Array.isArray((raw as any)?.results)) return (raw as any).results as PublicUserAddress[];
  return [];
};

export const fetchPublicProjectMessages = async (
  token: string,
  projectId: string,
  publicToken: string,
  page = 1,
  limit = 20
): Promise<{ data: ProjectMessageRow[]; total: number }> => {
  const qs = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (publicToken) qs.set("token", publicToken);
  const response = await fetch(
    `${API_URL}/public/projects/${encodeURIComponent(projectId)}/messages?${qs.toString()}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg = (raw as any)?.error || (raw as any)?.message || "Failed to load public messages";
    throw new Error(typeof msg === "string" ? msg : "Failed to load public messages");
  }
  const rows = Array.isArray((raw as any)?.data) ? (raw as any).data : [];
  return {
    data: rows,
    total: typeof (raw as any)?.total === "number" ? (raw as any).total : rows.length,
  };
};

export type ProjectApprovedMessageVersionSummary = {
  version?: number;
  summary?: string;
};

export type ProjectApprovedMessageData = {
  message_id?: string;
  project_id?: string;
  approved_proof_version?: number;
  summary?: string;
  version_summaries?: ProjectApprovedMessageVersionSummary[];
  billing_address?: any;
  shipping_address?: any;
  quantity?: number;
  shipping_method?: { name?: string; description?: string };
  shipping_amount?: number;
  approved_files?: MessageReplyFile[];
  approved_proofs?: Array<{ version?: number; summary?: string; order_summary?: string }>;
};

/** Internal summary text for an approved proof version (from approved-message payload). */
export function resolveApprovedProofVersionSummary(
  approvedRoot: Record<string, unknown> | null | undefined,
  version: number,
  firstFile?: Record<string, unknown> | null
): string {
  if (!approvedRoot) return "";
  const v = Math.floor(Number(version));
  if (!Number.isFinite(v) || v <= 0) return "";

  const versionSummaries = Array.isArray(approvedRoot.version_summaries)
    ? approvedRoot.version_summaries
    : [];
  const fromVersionList = versionSummaries.find(
    (row) => Math.floor(Number(row?.version)) === v
  );
  if (fromVersionList) {
    const text = String(fromVersionList.summary ?? "").trim();
    if (text) return text;
  }

  const approvedProofVersion = Math.floor(Number(approvedRoot.approved_proof_version));
  const rootSummary = String(approvedRoot.summary ?? approvedRoot.order_summary ?? "").trim();
  if (rootSummary && approvedProofVersion === v) return rootSummary;

  const proofs = Array.isArray(approvedRoot.approved_proofs)
    ? approvedRoot.approved_proofs
    : [];
  const proofRow = proofs.find((row) => Math.floor(Number(row?.version)) === v);
  if (proofRow) {
    const text = String(proofRow.summary ?? proofRow.order_summary ?? "").trim();
    if (text) return text;
  }

  return String(firstFile?.summary ?? firstFile?.order_summary ?? "").trim();
}

export type ProjectApprovedMessageResponse = {
  has_approved_message: boolean;
  message?: string;
  data?: ProjectApprovedMessageData | null;
};

/**
 * GET /projects/:projectId/approved-message
 * Works for both private and public access (public token can be passed as query param).
 */
export const fetchProjectApprovedMessage = async (
  token: string,
  projectId: string,
  publicToken = ""
): Promise<ProjectApprovedMessageResponse> => {
  const pid = String(projectId || "").trim();
  if (!pid) {
    return { has_approved_message: false, message: "Missing project id", data: null };
  }
  const qs = new URLSearchParams();
  if (publicToken) qs.set("token", publicToken);
  const query = qs.toString();
  const response = await fetch(
    `${API_URL}/public/projects/${encodeURIComponent(pid)}/approved-message${query ? `?${query}` : ""}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg = (raw as any)?.error || (raw as any)?.message || "Failed to load approved message";
    throw new Error(typeof msg === "string" ? msg : "Failed to load approved message");
  }
  const row = ((raw as any)?.data ?? (raw as any)?.approved_message ?? (raw as any)?.message_data ?? null) as
    | ProjectApprovedMessageData
    | null;
  return {
    has_approved_message: Boolean((raw as any)?.has_approved_message),
    message: typeof (raw as any)?.message === "string" ? (raw as any).message : undefined,
    data: row,
  };
};

export const fetchPublicProjectFiles = async (
  token: string,
  projectId: string,
  publicToken: string
): Promise<{
  shared_files: ProjectSharedFileRow[];
  /** Proof files keyed by version string (e.g. `"1"`), when returned by the public files endpoint. */
  proofs?: Record<string, ProjectSharedFileRow[]>;
}> => {
  const qs = new URLSearchParams();
  if (publicToken) qs.set("token", publicToken);
  const response = await fetch(
    `${API_URL}/public/projects/${encodeURIComponent(projectId)}/files?${qs.toString()}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg = (raw as any)?.error || (raw as any)?.message || "Failed to load public files";
    throw new Error(typeof msg === "string" ? msg : "Failed to load public files");
  }
  const shared = Array.isArray((raw as any)?.shared_files) ? (raw as any).shared_files : [];
  const proofsRaw = (raw as any)?.proofs;
  const proofs =
    proofsRaw && typeof proofsRaw === "object" && !Array.isArray(proofsRaw)
      ? (proofsRaw as Record<string, ProjectSharedFileRow[]>)
      : undefined;
  return { shared_files: shared, proofs };
};

export const fetchPublicProjectProofsSelect2 = async (
  token: string,
  projectId: string,
  publicToken: string,
  page = 1,
  limit = 10
): Promise<{ results: ProjectProofSelect2Row[]; hasMore: boolean }> => {
  const qs = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (publicToken) qs.set("token", publicToken);
  const response = await fetch(
    `${API_URL}/public/projects/${encodeURIComponent(projectId)}/proofs/select2?${qs.toString()}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg = (raw as any)?.error || (raw as any)?.message || "Failed to load public proofs";
    throw new Error(typeof msg === "string" ? msg : "Failed to load public proofs");
  }
  const list = Array.isArray((raw as any)?.results) ? (raw as any).results : [];
  return {
    results: list,
    hasMore: Boolean((raw as any)?.pagination?.more),
  };
};

export const postPublicProjectMessage = async (
  token: string,
  projectId: string,
  publicToken: string,
  payload: {
    message: string;
    reply_to_file_ids?: string[];
    sender_id?: string;
    sender_name?: string;
    created_by?: {
      id?: string;
      name?: string;
    };
  }
): Promise<unknown> => {
  const qs = new URLSearchParams();
  if (publicToken) qs.set("token", publicToken);
  const response = await fetch(
    `${API_URL}/public/projects/${encodeURIComponent(projectId)}/messages${qs.toString() ? `?${qs}` : ""}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg = (raw as any)?.error || (raw as any)?.message || "Failed to send response";
    throw new Error(typeof msg === "string" ? msg : "Failed to send response");
  }
  return raw;
};

export const uploadPublicProjectFiles = async (
  token: string,
  projectId: string,
  publicToken: string,
  files: File[],
  opts?: { is_proof?: boolean; message_id?: string }
): Promise<unknown> => {
  if (!files.length) return null;
  const fd = new FormData();
  files.forEach((f) => fd.append("files[]", f));
  if (typeof opts?.is_proof === "boolean") fd.append("is_proof", opts.is_proof ? "true" : "false");
  if (typeof opts?.message_id === "string") fd.append("message_id", opts.message_id);
  const qs = new URLSearchParams();
  if (publicToken) qs.set("token", publicToken);
  const response = await fetch(
    `${API_URL}/public/projects/${encodeURIComponent(projectId)}/files${qs.toString() ? `?${qs}` : ""}`,
    {
      method: "POST",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: fd,
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg = (raw as any)?.error || (raw as any)?.message || "Failed to upload attachment";
    throw new Error(typeof msg === "string" ? msg : "Failed to upload attachment");
  }
  return raw;
};

const normalizeApprovalAddressDetails = (details: unknown): Record<string, unknown> | undefined => {
  if (!details || typeof details !== "object") return undefined;
  const d = { ...(details as Record<string, unknown>) };
  const line1 = String(d.addressLine1 ?? d.address_line_1 ?? "").trim();
  const line2 = String(d.addressLine2 ?? d.address_line_2 ?? "").trim();
  delete d.address_line_1;
  delete d.address_line_2;
  return { ...d, addressLine1: line1, addressLine2: line2 };
};

const normalizeApprovalAddressPayload = (addr: unknown): unknown => {
  if (addr == null || typeof addr !== "object") return addr;
  const normalized = { ...(addr as Record<string, unknown>) };
  if (normalized.address_details != null) {
    normalized.address_details = normalizeApprovalAddressDetails(normalized.address_details);
  }
  return normalized;
};

const normalizeApproveProofPayload = (payload: Record<string, unknown>): Record<string, unknown> => {
  const body = { ...payload };
  if ("billing_address" in body) {
    body.billing_address = normalizeApprovalAddressPayload(body.billing_address);
  }
  if ("shipping_address" in body) {
    body.shipping_address = normalizeApprovalAddressPayload(body.shipping_address);
  }
  return body;
};

export const approvePublicProjectProof = async (
  token: string,
  projectId: string,
  publicToken: string,
  version: number,
  approvedBy?: { id: string; name: string },
  extraPayload?: Record<string, unknown>
): Promise<unknown> => {
  const qs = new URLSearchParams();
  if (publicToken) qs.set("token", publicToken);
  // New backend contract supports bulk versions + file_quantities.
  // Start from caller payload so fields like `versions` and `file_quantities`
  // are preserved exactly as provided.
  const body: Record<string, unknown> =
    extraPayload && typeof extraPayload === "object" ? { ...extraPayload } : {};
  if (!Array.isArray(body.versions) || body.versions.length === 0) {
    body.versions = [version];
  }
  if (approvedBy) {
    const actor = {
      id: String(approvedBy.id ?? "").trim(),
      name: String(approvedBy.name ?? "").trim(),
    };
    body.updated_by = actor;
  }
  const response = await fetch(
    `${API_URL}/public/projects/${encodeURIComponent(projectId)}/approve-proof${qs.toString() ? `?${qs}` : ""}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(normalizeApproveProofPayload(body)),
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg = (raw as any)?.error || (raw as any)?.message || "Failed to approve proof";
    throw new Error(typeof msg === "string" ? msg : "Failed to approve proof");
  }
  return raw;
};

/** PUT /public/projects/:projectId/approved-proof-details?token= (billing, shipping, file quantities, etc.) */
export const updatePublicApprovedProofDetails = async (
  token: string,
  projectId: string,
  publicToken: string,
  payload: Record<string, unknown>
): Promise<unknown> => {
  const qs = new URLSearchParams();
  if (publicToken) qs.set("token", publicToken);
  const response = await fetch(
    `${API_URL}/public/projects/${encodeURIComponent(projectId)}/approved-proof-details${qs.toString() ? `?${qs}` : ""}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(
        normalizeApproveProofPayload(
          payload && typeof payload === "object" ? { ...payload } : {}
        )
      ),
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg = (raw as any)?.error || (raw as any)?.message || "Failed to update approved proof details";
    throw new Error(typeof msg === "string" ? msg : "Failed to update approved proof details");
  }
  return raw;
};

/** PATCH /public/projects/:projectId/approved-proof-summary?token= */
export const patchPublicApprovedProofSummary = async (
  token: string,
  projectId: string,
  publicToken: string,
  payload: { version: number; summary: string }
): Promise<unknown> => {
  const v = Math.floor(Number(payload.version));
  if (!Number.isFinite(v) || v <= 0) {
    throw new Error("A valid proof version is required.");
  }
  const qs = new URLSearchParams();
  if (publicToken) qs.set("token", publicToken);
  const response = await fetch(
    `${API_URL}/public/projects/${encodeURIComponent(projectId)}/approved-proof-summary${qs.toString() ? `?${qs}` : ""}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        version: v,
        summary: String(payload.summary ?? "").trim(),
      }),
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg = (raw as any)?.error || (raw as any)?.message || "Failed to update approved proof summary";
    throw new Error(typeof msg === "string" ? msg : "Failed to update approved proof summary");
  }
  return raw;
};

/** Save internal summary note for an approved proof version. */
export const updatePublicApprovedProofOrderSummary = async (
  token: string,
  projectId: string,
  publicToken: string,
  version: number,
  summary: string
): Promise<unknown> => {
  return patchPublicApprovedProofSummary(token, projectId, publicToken, { version, summary });
};

/**
 * Public GET order may return `{ order: {...}, project_settings?: {...} }` instead of a flat order document.
 * UI and email code expect one object with order fields + optional `project_settings` on the same level.
 */
export function normalizePublicOrderOrInstallationPayload(raw: unknown): unknown {
  if (!raw || typeof raw !== "object") return raw;
  const r = raw as { order?: Record<string, unknown>; project_settings?: Record<string, unknown> };
  const inner = r.order;
  if (inner && typeof inner === "object") {
    const ps = r.project_settings;
    return {
      ...inner,
      project_settings:
        ps && typeof ps === "object"
          ? ps
          : (inner as { project_settings?: Record<string, unknown> }).project_settings,
    };
  }
  return raw;
}

export const fetchPublicProjectOrderById = async (
  token: string,
  projectId: string,
  orderId: string,
  publicToken: string
): Promise<any> => {
  const qs = new URLSearchParams();
  if (publicToken) qs.set("token", publicToken);
  const response = await fetch(
  // ${API_URL}/public/projects/${encodeURIComponent(projectId)}/orders/${encodeURIComponent(orderId)}
    `${API_URL}/public/orders/${encodeURIComponent(orderId)}${
      qs.toString() ? `?${qs.toString()}` : ""
    }`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg = (raw as any)?.error || (raw as any)?.message || "Failed to load public order";
    throw new Error(typeof msg === "string" ? msg : "Failed to load public order");
  }
  return normalizePublicOrderOrInstallationPayload(raw);
};

/** GET /public/projects/:projectId/installation_orders/:installationOrderId?token= */
export const fetchPublicInstallationOrderById = async (
  token: string,
  projectId: string,
  installationOrderId: string,
  publicToken: string
): Promise<any> => {
  const qs = new URLSearchParams();
  if (publicToken) qs.set("token", publicToken);
  const response = await fetch(
        // `${API_URL}/public/projects/${encodeURIComponent(projectId)}/installation_orders/${encodeURIComponent(

    `${API_URL}/public/installation_orders/${encodeURIComponent(
      installationOrderId
    )}${qs.toString() ? `?${qs.toString()}` : ""}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg = (raw as any)?.error || (raw as any)?.message || "Failed to load public installation order";
    throw new Error(typeof msg === "string" ? msg : "Failed to load public installation order");
  }
  return normalizePublicOrderOrInstallationPayload(raw);
};

/** POST /public/projects/:projectId/installation_orders/:installationOrderId/payment?token= */
export const postPublicInstallationOrderPayment = async (
  projectId: string,
  installationOrderId: string,
  publicToken: string,
  body: Record<string, unknown>
): Promise<Response> => {
  const qs = new URLSearchParams();
  if (publicToken) qs.set("token", publicToken);
  const q = qs.toString();
  return fetch(
    // projects/${encodeURIComponent(projectId)}
    `${API_URL}/public/installation_orders/${encodeURIComponent(
      installationOrderId
    )}/payment${q ? `?${q}` : ""}`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  );
};

/** GET /public/removal_orders/:removalOrderId?token= */
export const fetchPublicRemovalOrderById = async (
  token: string,
  projectId: string,
  removalOrderId: string,
  publicToken: string
): Promise<any> => {
  const qs = new URLSearchParams();
  if (publicToken) qs.set("token", publicToken);
  const response = await fetch(
    `${API_URL}/public/removal_orders/${encodeURIComponent(removalOrderId)}${
      qs.toString() ? `?${qs.toString()}` : ""
    }`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg = (raw as any)?.error || (raw as any)?.message || "Failed to load public removal order";
    throw new Error(typeof msg === "string" ? msg : "Failed to load public removal order");
  }
  return normalizePublicOrderOrInstallationPayload(raw);
};

/** POST /public/removal_orders/:removalOrderId/payment?token= */
export const postPublicRemovalOrderPayment = async (
  projectId: string,
  removalOrderId: string,
  publicToken: string,
  body: Record<string, unknown>
): Promise<Response> => {
  const qs = new URLSearchParams();
  if (publicToken) qs.set("token", publicToken);
  const q = qs.toString();
  return fetch(
    `${API_URL}/public/removal_orders/${encodeURIComponent(removalOrderId)}/payment${q ? `?${q}` : ""}`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  );
};

/* --------------------------- Projects list (datatable) --------------------- */

const ZERO_AGENT_ID = "000000000000000000000000";

export function getProjectAgentOrTeamMemberName(
  row: Pick<SignageProjectListItem, "agent_id" | "agent_name" | "team_member_name">
): string {
  const agentName = String(row.agent_name ?? "").trim();
  const teamMemberName = String(row.team_member_name ?? "").trim();
  const agentId = String(row.agent_id ?? "").trim();
  const hasValidAgentId = Boolean(agentId && agentId !== ZERO_AGENT_ID);

  if (hasValidAgentId && agentName) return agentName;
  if (teamMemberName) return teamMemberName;
  if (agentName) return agentName;
  return "—";
}

export type SignageProjectListItem = {
  id: string;
  project_number?: string;
  linked_order_id?: string;
  linked_order_number?: string;
  linked_order_type?: "sales_order" | "service_order";
  title?: string;
  agent_id?: string;
  agent_name?: string;
  team_member_name?: string;
  status?: string;
  payment_status?: string;
  waiting_on?: string;
  last_update?: string;
  designer_name?: string;
  assigned_to_name?: string;
  last_message?: {
    _id?: string;
    message?: string;
    sender_id?: string;
    sender_name?: string;
    sender_type?: string;
    created_at?: string;
  } | null;
  has_unread?: boolean;
  approved_update_seen_by_vendor?: boolean;
};

function asProjectRecord(value: unknown): Record<string, any> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, any>)
    : {};
}

function coerceProjectId(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object") {
    const rec = value as Record<string, unknown>;
    if (typeof rec.$oid === "string") return rec.$oid.trim();
    if (typeof rec.id === "string") return rec.id.trim();
    if (typeof rec._id === "string") return rec._id.trim();
  }
  return String(value).trim();
}

function normalizeSignageProjectListRow(row: unknown): SignageProjectListItem | null {
  const root = asProjectRecord(row);
  const id = coerceProjectId(root.id ?? root._id);
  if (!id) return null;
  const lastMessage = asProjectRecord(root.last_message);
  const linkedType = String(root.linked_order_type ?? "").trim().toLowerCase();
  return {
    id,
    project_number: String(root.project_number ?? "").trim() || undefined,
    linked_order_id: coerceProjectId(root.linked_order_id) || undefined,
    linked_order_number: String(root.linked_order_number ?? "").trim() || undefined,
    linked_order_type:
      linkedType === "service_order" || linkedType === "sales_order"
        ? linkedType
        : undefined,
    title: String(root.title ?? "").trim() || undefined,
    agent_id: coerceProjectId(root.agent_id) || undefined,
    agent_name: String(root.agent_name ?? "").trim() || undefined,
    team_member_name: String(root.team_member_name ?? "").trim() || undefined,
    status: String(root.status ?? "").trim() || undefined,
    payment_status: String(root.payment_status ?? "").trim() || undefined,
    waiting_on: String(root.waiting_on ?? root.dependency_on ?? "").trim() || undefined,
    last_update: String(root.last_update ?? root.updated_at ?? "").trim() || undefined,
    designer_name: String(root.designer_name ?? "").trim() || undefined,
    assigned_to_name: String(root.assigned_to_name ?? "").trim() || undefined,
    last_message: lastMessage.message
      ? {
          _id: coerceProjectId(lastMessage._id ?? lastMessage.id) || undefined,
          message: String(lastMessage.message ?? ""),
          sender_id: coerceProjectId(lastMessage.sender_id) || undefined,
          sender_name: String(lastMessage.sender_name ?? ""),
          sender_type: String(lastMessage.sender_type ?? ""),
          created_at: String(lastMessage.created_at ?? ""),
        }
      : null,
    has_unread: root.has_unread === true,
    approved_update_seen_by_vendor:
      typeof root.approved_update_seen_by_vendor === "boolean"
        ? root.approved_update_seen_by_vendor
        : undefined,
  };
}

export function getLinkedOrderViewPath(
  orderBaseRoute: string,
  params: Pick<SignageProjectListItem, "linked_order_id" | "linked_order_type">
): string | null {
  const orderId = String(params.linked_order_id ?? "").trim();
  if (!orderId) return null;
  const orderType = String(params.linked_order_type ?? "sales_order").trim().toLowerCase();
  if (orderType === "service_order") {
    return `${orderBaseRoute}/jobs/installationWorkOrder/${encodeURIComponent(orderId)}`;
  }
  return `${orderBaseRoute}/orderDetails/${encodeURIComponent(orderId)}`;
}

export type SignageProjectsListCounts = {
  in_progress: number;
  design_approved: number;
  quoted: number;
  quote_approved: number;
  completed: number;
  total: number;
};

export type FetchSignageProjectsResult = {
  data: SignageProjectListItem[];
  total: number;
  counts: SignageProjectsListCounts | null;
  page: number;
  limit: number;
};

/**
 * POST /projects/datatable
 */
export const fetchSignageProjectsList = async (
  token: string,
  params: { page: number; limit: number; search?: string; status?: string }
): Promise<FetchSignageProjectsResult> => {
  const response = await fetch(`${API_URL}/projects/datatable`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      page: params.page,
      limit: params.limit,
      search: (params.search ?? "").trim(),
      filters: {
        status: (params.status ?? "").trim().toLowerCase(),
      },
      sort: {
        field: "updated_at",
        order: "desc",
      },
    }),
  });

  const raw = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message =
      (raw as any)?.error || (raw as any)?.message || "Failed to load projects";
    throw new Error(typeof message === "string" ? message : "Failed to load projects");
  }

  const root = asProjectRecord(raw);
  const nested = asProjectRecord(root.data);
  const rows: unknown[] = Array.isArray(root.data)
    ? root.data
    : Array.isArray(nested.data)
      ? nested.data
      : Array.isArray(root.items)
        ? root.items
        : Array.isArray(root.results)
          ? root.results
          : [];
  const list = rows
    .map(normalizeSignageProjectListRow)
    .filter((row): row is SignageProjectListItem => !!row);

  const counts = asProjectRecord(root.counts ?? nested.counts);
  const total =
    Number(root.total ?? nested.total ?? root.recordsFiltered ?? root.recordsTotal ?? list.length) || 0;
  const normalizedCounts: SignageProjectsListCounts | null =
    Object.keys(counts).length > 0
      ? {
          in_progress: Number(counts.in_progress) || 0,
          design_approved: Number(counts.design_approved) || 0,
          quoted: Number(counts.quoted) || 0,
          quote_approved: Number(counts.quote_approved) || 0,
          completed: Number(counts.completed) || 0,
          total: Number(counts.total) || total,
        }
      : null;

  return {
    data: list,
    total,
    counts: normalizedCounts,
    page: Number(root.page ?? nested.page ?? params.page) || params.page,
    limit: Number(root.limit ?? nested.limit ?? params.limit) || params.limit,
  };
};

/* ------------------------ Project view (detail / messages / files) -------- */

export type SignageProjectDetail = {
  id: string;
  project_number?: string;
  address_count?: number;
  title?: string;
  notes?: string;
  po_number?: string;
  customer_name?: string;
  customer_id?: string;
  customer_phone?: string;
  first_name?: string;
  last_name?: string;
  vendor_id?: string;
  owner_node_id?: string;
  owner_node_type?: string;
  agent_id?: string;
  agent_name?: string;
  team_member_id?: string;
  team_member_name?: string;
  customer_email?: string;
  /** When present, used for BROKERAGE_TEAM_MEMBER email copies (e.g. order summary, conversation notices). */
  team_member_email?: string;
  brokerage_firm_id?: string;
  brokerage_firm_name?: string;
  /** When present, used for BROKERAGE_FIRM email copies (e.g. project create, conversation notices). */
  brokerage_firm_email?: string;
  /** Project property / site address (e.g. from POST /projects). */
  address?: SignageProjectAddressPayload;
  office?: {
    _id?: string;
    /** API often returns `name`; legacy payloads may use `officeName`. */
    name?: string;
    officeName?: string;
    officeType?: string;
    /** Single object or array of address rows from API. */
    officeAddress?: Record<string, unknown> | Record<string, unknown>[];
  };
  designer_id?: string;
  designer_name?: string;
  /** Person responsible for quoting / order creation (notify after design approval). */
  payment_email?: string;
  assigned_to_name?: string;
  status?: string;
  linked_order_type?: "sales_order" | "service_order";
  dependency_on?: string;
  last_message_at?: string;
  send_message_to_client?: boolean;
  mute_messages_to_client?: boolean;
  /** When false, skip customer/assignee notification emails for this project. */
  mail_notify?: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: { id?: string; name?: string };
  updated_by?: { id?: string; name?: string };
  /** Party license category for this project (from create/search). */
  license_type?: string;
};

/**
 * PUT /projects: use stored project `address` when present; otherwise resolve via search item + office/firm (same as create).
 */
export async function resolveSignageProjectAddressForUpdate(
  token: string,
  opts: {
    existingProject: SignageProjectDetail | null;
    searchItem?: unknown;
    office: { _id: string; officeName?: string; name?: string } | null;
    brokerageFirmId: string;
    projectTitle: string;
    customerPhone: string;
  }
): Promise<SignageProjectAddressPayload> {
  const direct = opts.existingProject?.address;
  if (direct && typeof direct === "object" && !Array.isArray(direct)) {
    return toSignageProjectAddressPayload(direct as Record<string, unknown>);
  }
  return resolveSignageProjectAddressForCreate(token, {
    searchItem: opts.searchItem ?? {},
    office: opts.office,
    brokerageFirmId: opts.brokerageFirmId,
    projectTitle: opts.projectTitle,
    customerPhone: opts.customerPhone,
  });
}

export type GeneralSettingByKeyRow = {
  _id?: string;
  description?: string;
  enabled?: boolean;
  exists?: boolean;
  key?: string;
  updated_at?: string;
  owner_node_id?: string;
  owner_node_type?: string;
  value_type?: string;
  /** Some by-key settings return a list of notification emails. */
  emails?: string[];
  string_array_value?: string[];
  list_of_emails?: string[];
  value?: unknown;
};

export type GeneralSettingByKeyResponse = {
  setting?: GeneralSettingByKeyRow;
  /** Public by-key may return the row at the top level (not under `setting`). */
  enabled?: boolean;
  exists?: boolean;
  key?: string;
  emails?: string[];
  string_array_value?: string[];
};

/** Private `setting` wrapper or public flat row — read `enabled` either way. */
export function parseGeneralSettingEnabledFromByKeyResponse(
  raw: GeneralSettingByKeyResponse | Record<string, unknown> | null | undefined
): boolean {
  if (!raw || typeof raw !== "object") return false;
  const anyRaw = raw as GeneralSettingByKeyResponse & Record<string, unknown>;
  const nested =
    anyRaw.setting && typeof anyRaw.setting === "object" ? anyRaw.setting : null;
  if (nested && typeof nested.enabled === "boolean") return nested.enabled;
  if (typeof anyRaw.enabled === "boolean") return anyRaw.enabled;
  return false;
}

function normalizeGeneralSettingRowFromByKeyResponse(
  raw: GeneralSettingByKeyResponse | Record<string, unknown> | null | undefined
): GeneralSettingByKeyRow | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const anyRaw = raw as GeneralSettingByKeyResponse & Record<string, unknown>;
  if (anyRaw.setting && typeof anyRaw.setting === "object") return anyRaw.setting;
  if (typeof anyRaw.enabled === "boolean" || anyRaw.key) {
    return anyRaw as GeneralSettingByKeyRow;
  }
  return undefined;
}

/** General settings keys for order-type notification email recipients. */
export const SALES_ORDER_NOTIFICATION_EMAILS_KEY = "sales_order.notification_emails";
export const INSTALLATION_ORDER_NOTIFICATION_EMAILS_KEY = "installation_order.notification_emails";
export const REMOVAL_ORDER_NOTIFICATION_EMAILS_KEY = "removal_order.notification_emails";

/** @deprecated Prefer SALES_ORDER_NOTIFICATION_EMAILS_KEY */
export const ORDER_JOB_NOTIFICATION_EMAILS_KEY = "order_job.notification_emails";

export type PatchGeneralNotificationEmailsBody = {
  key: string;
  emails: {
    add: Array<{
      license_type: string;
      value: string;
    }>;
    remove: Array<{
      license_type: string;
      value: string;
    }>;
  };
  enabled: boolean;
};

function parseEmailsFromGeneralSettingByKeyRaw(raw: unknown): { enabled: boolean; emails: string[] } {
  const r = raw as Record<string, unknown>;
  const setting = (r?.setting ?? null) as Record<string, unknown> | null;
  let emails: string[] = [];

  const toEmailString = (entry: unknown): string => {
    if (typeof entry === "string") return entry.trim();
    if (entry && typeof entry === "object") {
      const value = (entry as { value?: unknown }).value;
      return typeof value === "string" ? value.trim() : "";
    }
    return "";
  };

  const pushFrom = (v: unknown) => {
    if (Array.isArray(v)) {
      emails = v.map((x) => toEmailString(x)).filter(Boolean);
    }
  };

  if (setting && typeof setting === "object") {
    pushFrom(setting.emails);
    if (!emails.length) pushFrom(setting.string_array_value);
    if (!emails.length && setting.value != null) {
      if (Array.isArray(setting.value)) pushFrom(setting.value);
      else if (typeof setting.value === "string") {
        try {
          const parsed = JSON.parse(setting.value) as unknown;
          pushFrom(parsed);
        } catch {
          /* ignore */
        }
      }
    }
  }
  if (!emails.length) pushFrom(r.emails);
  if (!emails.length) pushFrom(r.string_array_value);

  const uniq = Array.from(new Set(emails.map((e) => e.trim()).filter(Boolean)));
  const enabled = Boolean(
    (setting && typeof setting === "object" && setting.enabled === true) ||
      r.enabled === true ||
      uniq.length > 0
  );
  return { enabled, emails: uniq };
}

/** GET /settings/general/bykey?key=<notification_emails_key> */
export const fetchNotificationEmailsSettingByKey = async (
  token: string,
  key: string
): Promise<{ enabled: boolean; emails: string[] }> => {
  const response = await fetch(
    `${API_URL}/settings/general/bykey?key=${encodeURIComponent(key)}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (response.status === 404) {
      return { enabled: false, emails: [] };
    }
    const message =
      (raw as any)?.error || (raw as any)?.message || "Failed to load notification emails setting";
    throw new Error(typeof message === "string" ? message : "Failed to load notification emails setting");
  }
  return parseEmailsFromGeneralSettingByKeyRaw(raw);
};

/** @deprecated Use fetchNotificationEmailsSettingByKey(token, SALES_ORDER_NOTIFICATION_EMAILS_KEY) */
export const fetchOrderJobNotificationEmailsSetting = async (
  token: string
): Promise<{ enabled: boolean; emails: string[] }> =>
  fetchNotificationEmailsSettingByKey(token, SALES_ORDER_NOTIFICATION_EMAILS_KEY);

/** PATCH /settings/general/notification-emails */
export const patchGeneralNotificationEmails = async (
  token: string,
  body: PatchGeneralNotificationEmailsBody
): Promise<void> => {
  const response = await fetch(`${API_URL}/settings/general/notification-emails`, {
    method: "PATCH",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      (raw as any)?.error || (raw as any)?.message || "Failed to update notification emails";
    throw new Error(typeof message === "string" ? message : "Failed to update notification emails");
  }
};

export const ORDER_QUOTE_MODE_SETTING_KEY = "order.quote_mode";

/** GET /settings/general/bykey?key=order.quote_mode */
export const fetchOrderQuoteModeSetting = async (
  token: string
): Promise<{ enabled: boolean; setting?: GeneralSettingByKeyResponse["setting"] }> => {
  const response = await fetch(
    `${API_URL}/settings/general/bykey?key=${encodeURIComponent(ORDER_QUOTE_MODE_SETTING_KEY)}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );
  const raw: GeneralSettingByKeyResponse = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      (raw as any)?.error || (raw as any)?.message || "Failed to load quote mode setting";
    throw new Error(typeof message === "string" ? message : "Failed to load quote mode setting");
  }
  return {
    enabled: parseGeneralSettingEnabledFromByKeyResponse(raw),
    setting: normalizeGeneralSettingRowFromByKeyResponse(raw),
  };
};

/** Public: GET /public/settings/general/bykey?owner_node_id=…&key=order.quote_mode — no auth. */
export const fetchPublicOrderQuoteModeSetting = async (
  ownerNodeId: string
): Promise<{ enabled: boolean; setting?: GeneralSettingByKeyRow }> => {
  const owner = String(ownerNodeId || "").trim();
  if (!owner) {
    return { enabled: false };
  }
  const response = await fetch(
    `${API_URL}/public/settings/general/bykey?owner_node_id=${encodeURIComponent(owner)}&key=${encodeURIComponent(ORDER_QUOTE_MODE_SETTING_KEY)}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    }
  );
  const raw: GeneralSettingByKeyResponse = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      (raw as any)?.error || (raw as any)?.message || "Failed to load quote mode setting";
    throw new Error(typeof message === "string" ? message : "Failed to load quote mode setting");
  }
  return {
    enabled: parseGeneralSettingEnabledFromByKeyResponse(raw),
    setting: normalizeGeneralSettingRowFromByKeyResponse(raw),
  };
};

/** POST /project-settings — quote auto-assign after design approval (general settings). */
export type ProjectSettingsQuoteAssignPayload = {
  quote_assign_enabled: boolean;
  quote_assignee_id: string;
  quote_assignee_name: string;
  /** Selected quote assignee mailbox (team member email). */
  payment_email: string;
  mail_notify: boolean;
  licenseType: string;
};

export const postProjectSettingsQuoteAssign = async (
  token: string,
  payload: ProjectSettingsQuoteAssignPayload
): Promise<void> => {
  const response = await fetch(`${API_URL}/project-settings`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      (raw as any)?.error || (raw as any)?.message || "Failed to save quote assign settings";
    throw new Error(typeof message === "string" ? message : "Failed to save quote assign settings");
  }
};

export const fetchProjectById = async (
  token: string,
  projectId: string
): Promise<SignageProjectDetail> => {
  const response = await fetch(`${API_URL}/projects/${encodeURIComponent(projectId)}`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      (raw as any)?.error || (raw as any)?.message || "Failed to load project";
    throw new Error(typeof message === "string" ? message : "Failed to load project");
  }
  return raw as SignageProjectDetail;
};

/** POST /projects/:projectId/reopen */
export const reopenProject = async (
  token: string,
  projectId: string
): Promise<SignageProjectDetail> => {
  const response = await fetch(`${API_URL}/projects/${encodeURIComponent(projectId)}/reopen`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      (raw as any)?.error || (raw as any)?.message || "Failed to reopen project";
    throw new Error(typeof message === "string" ? message : "Failed to reopen project");
  }
  return raw as SignageProjectDetail;
};

/** GET /signage/:signageId/projects/select2 — project picker for sales orders (Select2-style). */
export async function fetchProjectsSelect2(
  token: string,
  signageId: string,
  params: { page?: number; limit?: number; search?: string }
): Promise<{ list: any[]; hasMore: boolean }> {
  const page = params.page ?? 1;
  const limit = params.limit ?? 20;
  const qs = new URLSearchParams();
  qs.set("page", String(page));
  qs.set("limit", String(limit));
  const search = (params.search ?? "").trim();
  if (search) qs.set("search", search);
  const response = await fetch(
    `${API_URL}/signage/${encodeURIComponent(signageId)}/projects/select2?only_unassigned=true&${qs.toString()}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = (raw as any)?.error || (raw as any)?.message || "Failed to load projects";
    throw new Error(typeof message === "string" ? message : "Failed to load projects");
  }
  const list = Array.isArray(raw) ? raw : (raw as any)?.data ?? (raw as any)?.results ?? [];
  const hasMore = Array.isArray(raw)
    ? list.length === limit
    : Boolean((raw as any)?.pagination?.more) ||
      (typeof (raw as any)?.total === "number" && page * limit < Number((raw as any).total));
  return { list, hasMore: Boolean(hasMore) };
}

/** File objects nested on GET /projects/:id/messages when message references uploads. */
export type MessageReplyFile = {
  id?: string;
  _id?: string;
  /** Some payloads use `file_id` for the work-drive file document id. */
  file_id?: string;
  file_name?: string;
  original_name?: string;
  file_url?: string;
  preview_url?: string;
  file_type?: string;
  file_size?: number;
  is_proof?: boolean;
  version?: number;
  is_approved?: boolean;
  approved_at?: string;
  design_approved_by?: { id?: string; name?: string };
  design_approved_at?: string;
};

export type ProjectMessageRow = {
  /** API may return `id` or `_id`. */
  id?: string;
  _id?: string;
  project_id?: string;
  sender_id?: string;
  sender_name?: string;
  sender_type?: string;
  message?: string;
  reply_to_message_id?: string;
  reply_to_file_ids?: string[];
  reply_to_files?: MessageReplyFile[];
  /** When set, conversation UI shows approved proof treatment for this message. */
  approved_by?: { id?: string; name?: string };
  design_approved_by?: { id?: string; name?: string };
  design_approved_at?: string;
  billing_address?: any;
  shipping_address?: any;
  both_address_same?: boolean;
  quantity?: number;
  shipping_method?: { name?: string; description?: string };
  created_at?: string;
  is_seen_by_vendor?: boolean;
};

export const fetchProjectMessagesList = async (
  token: string,
  projectId: string,
  page = 1,
  limit = 20
): Promise<{ data: ProjectMessageRow[]; total: number }> => {
  const qs = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  const response = await fetch(
    `${API_URL}/projects/${encodeURIComponent(projectId)}/messages?${qs}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      (raw as any)?.error || (raw as any)?.message || "Failed to load messages";
    throw new Error(typeof message === "string" ? message : "Failed to load messages");
  }
  const data = Array.isArray((raw as any)?.data) ? (raw as any).data : [];
  return {
    data,
    total: typeof (raw as any)?.total === "number" ? (raw as any).total : data.length,
  };
};

/** GET /public/projects/:projectId/messages (public route; no token required). */
export const fetchPublicProjectMessagesByProjectId = async (
  projectId: string,
  page = 1,
  limit = 20
): Promise<{ data: ProjectMessageRow[]; total: number }> => {
  const pid = String(projectId || "").trim();
  if (!pid) return { data: [], total: 0 };
  const qs = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  const response = await fetch(
    `${API_URL}/public/projects/${encodeURIComponent(pid)}/messages?${qs.toString()}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      (raw as any)?.error || (raw as any)?.message || "Failed to load public project messages";
    throw new Error(typeof message === "string" ? message : "Failed to load public project messages");
  }
  const data = Array.isArray((raw as any)?.data) ? (raw as any).data : [];
  return {
    data,
    total: typeof (raw as any)?.total === "number" ? (raw as any).total : data.length,
  };
};

export type ProjectSharedFileRow = {
  _id: string;
  /** Work-drive document id — use for download/delete, not the join row `_id`. */
  file_id?: string;
  project_id?: string;
  file_name?: string;
  original_name?: string;
  file_url?: string;
  file_key?: string;
  file_type?: string;
  file_size?: number;
  is_proof?: boolean;
  /** Presigned or absolute URL for thumbnails / preview (preferred over building from file_url). */
  preview_url?: string;
  created_by?: { id?: string; name?: string };
  created_at?: string;
};

export const fetchSignageProjectFilesList = async (
  token: string,
  signageId: string,
  projectId: string
): Promise<{
  shared_files: ProjectSharedFileRow[];
  proofs?: Record<string, ProjectSharedFileRow[]>;
}> => {
  const response = await fetch(
    `${API_URL}/signage/${encodeURIComponent(signageId)}/projects/${encodeURIComponent(projectId)}/files`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      (raw as any)?.error || (raw as any)?.message || "Failed to load files";
    throw new Error(typeof message === "string" ? message : "Failed to load files");
  }
  const payload = (raw as any)?.data && typeof (raw as any).data === "object"
    ? (raw as any).data
    : (raw as any);
  const shared = Array.isArray(payload?.shared_files)
    ? payload.shared_files
    : [];
  const proofsRaw = payload?.proofs;
  const proofs =
    proofsRaw && typeof proofsRaw === "object" && !Array.isArray(proofsRaw)
      ? (proofsRaw as Record<string, ProjectSharedFileRow[]>)
      : undefined;
  return { shared_files: shared, proofs };
};

export type ProjectProofSelect2Row = {
  id: string;
  /** Work-drive document id — use for download/delete, not the proof join row `id`. */
  file_id?: string;
  file_name?: string;
  original_name?: string;
  file_url?: string;
  /** Presigned preview URL when API provides it. */
  preview_url?: string;
  file_type?: string;
  file_size?: number;
  quantity?: number;
  created_by?: { id?: string; name?: string };
  created_at?: string;
  version?: number;
  is_proof?: boolean;
  is_approved?: boolean;
};

export const fetchProjectProofsSelect2 = async (
  token: string,
  signageId: string,
  projectId: string,
  page = 1,
  limit = 10
): Promise<{ results: ProjectProofSelect2Row[]; hasMore: boolean }> => {
  const qs = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  const response = await fetch(
    `${API_URL}/signage/${encodeURIComponent(signageId)}/projects/${encodeURIComponent(projectId)}/proofs/select2?${qs}`,
    {
      method: "GET",
      cache: "no-store",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
  const raw = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      (raw as any)?.error || (raw as any)?.message || "Failed to load proofs";
    throw new Error(typeof message === "string" ? message : "Failed to load proofs");
  }
  const list: ProjectProofSelect2Row[] = Array.isArray((raw as any)?.results)
    ? (raw as any).results
    : [];
  return {
    results: list,
    hasMore: Boolean((raw as any)?.pagination?.more),
  };
};

/** Build absolute URL for project file paths returned by the API. */
export const projectAssetUrl = (fileUrl: string | undefined): string => {
  if (!fileUrl) return "";
  if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
  const base = (API_URL || "").replace(/\/+$/, "");
  const path = fileUrl.replace(/^\/+/, "").replace(/\\/g, "/");
  return `${base}/${path}`;
};

function isLikelyMongoObjectId(s: string): boolean {
  return /^[a-f\d]{24}$/i.test(String(s).trim());
}

/**
 * Public work-drive file download (no auth), e.g.
 * GET /api/v1/public/work-drive/files/:fileId/download
 */
export const publicWorkDriveFileDownloadUrl = (fileId: string | undefined): string => {
  const id = String(fileId ?? "").trim();
  if (!id) return "";
  const base = (API_URL || "").replace(/\/+$/, "");
  return `${base}/public/work-drive/files/${encodeURIComponent(id)}/download`;
};

/** Hard delete a work-drive file (S3 + DB). Requires auth token. */
export const hardDeleteWorkDriveFile = async (
  token: string,
  fileId: string | undefined
): Promise<void> => {
  const id = String(fileId ?? "").trim();
  if (!id) return;
  const t = String(token ?? "").trim();
  if (!t) throw new Error("Missing auth token.");
  const base = (API_URL || "").replace(/\/+$/, "");
  const response = await fetch(
    `${base}/work-drive/files/${encodeURIComponent(id)}/hard`,
    {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${t}`,
      },
    }
  );
  if (response.ok) return;
  const raw = await response.json().catch(() => ({}));
  const message =
    (raw as any)?.error || (raw as any)?.message || "Failed to delete file";
  throw new Error(typeof message === "string" ? message : "Failed to delete file");
};

/** DELETE /projects/:projectId/files/:projectFileId (authenticated signage/project file delete). */
export const deleteProjectFileByProject = async (
  token: string,
  projectId: string,
  projectFileId: string
): Promise<void> => {
  const t = String(token || "").trim();
  const pid = String(projectId || "").trim();
  const fid = String(projectFileId || "").trim();
  if (!t) throw new Error("Missing auth token.");
  if (!pid || !fid) throw new Error("Missing project file id.");
  const base = (API_URL || "").replace(/\/+$/, "");
  const response = await fetch(
    `${base}/projects/${encodeURIComponent(pid)}/files/${encodeURIComponent(fid)}`,
    {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${t}`,
      },
    }
  );
  if (response.ok) return;
  const raw = await response.json().catch(() => ({}));
  const message =
    (raw as any)?.error || (raw as any)?.message || "Failed to delete file";
  throw new Error(typeof message === "string" ? message : "Failed to delete file");
};

/**
 * Resolve a browser download URL for project / proof files:
 * 1) Prefer `file_id` (work-drive document id), then `id` / `_id` → public work-drive download
 * 2) Absolute `file_url` when no resolvable id
 * 3) Relative `file_url` joined with API base
 * 4) Any remaining id → work-drive download (fallback)
 */
export const resolveProjectFileDownloadUrl = (file: {
  id?: string;
  _id?: string;
  file_id?: string;
  file_url?: string;
}): string => {
  const rawId = String(file?.file_id ?? file?.id ?? file?._id ?? "").trim();
  if (isLikelyMongoObjectId(rawId)) {
    return publicWorkDriveFileDownloadUrl(rawId);
  }

  const raw = file?.file_url != null ? String(file.file_url).trim() : "";
  if (raw && /^https?:\/\//i.test(raw)) return raw;
  if (raw) return projectAssetUrl(raw);
  if (rawId) return publicWorkDriveFileDownloadUrl(rawId);
  return "";
};

